// Utility functions for timezone management

import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";

/**
 * Get timezone for a specific company ID from cookies (Client-side)
 * @param companyId - The company ID to get timezone for
 * @returns The timezone string or null if not found
 */
export function getCompanyTimezone(companyId: string): string | null {
  if (typeof document === "undefined") {
    // Server-side, return null
    return null;
  }

  const cookies = document.cookie.split(";");
  const timezoneCookie = cookies.find(cookie => cookie.trim().startsWith(`company_${companyId}=`));

  if (timezoneCookie) {
    return timezoneCookie.split("=")[1];
  }

  return null;
}

/**
 * Get all company timezones from cookies (Client-side)
 * @returns Object with company IDs as keys and timezones as values
 */
export function getAllCompanyTimezones(): Record<string, string> {
  if (typeof document === "undefined") {
    // Server-side, return empty object
    return {};
  }

  const timezones: Record<string, string> = {};
  const cookies = document.cookie.split(";");

  cookies.forEach(cookie => {
    const trimmed = cookie.trim();
    if (trimmed.startsWith("company_")) {
      const [name, value] = trimmed.split("=");
      const companyId = name.replace("company_", "");
      timezones[companyId] = value;
    }
  });

  return timezones;
}

/**
 * Server-side function to get timezone from cookies
 * @param cookieStore - Next.js cookies object (awaited)
 * @param companyId - The company ID to get timezone for
 * @returns The timezone string or null if not found
 */
export function getServerTimezone(cookieStore: any, companyId: string): string | null {
  const timezoneCookie = cookieStore.get(`company_${companyId}`);
  return timezoneCookie ? timezoneCookie.value : null;
}

/**
 * Server-side function to get all company timezones
 * @param cookieStore - Next.js cookies object (awaited)
 * @returns Object with company IDs as keys and timezones as values
 */
export function getAllServerTimezones(cookieStore: any): Record<string, string> {
  const timezones: Record<string, string> = {};

  // Get all cookies and filter for company timezone cookies
  const allCookies = cookieStore.getAll();

  allCookies.forEach((cookie: any) => {
    if (cookie.name.startsWith("company_")) {
      const companyId = cookie.name.replace("company_", "");
      timezones[companyId] = cookie.value;
    }
  });

  return timezones;
}

/**
 * Server-side async function to get timezone from cookies
 * @param companyId - The company ID to get timezone for
 * @returns The timezone string or null if not found
 */
export async function getServerTimezoneAsync(companyId: string): Promise<string | null> {
  const { cookies } = await import("next/headers");
  const cookieStore = await cookies();
  return getServerTimezone(cookieStore, companyId);
}

/**
 * Server-side async function to get all company timezones
 * @returns Object with company IDs as keys and timezones as values
 */
export async function getAllServerTimezonesAsync(): Promise<Record<string, string>> {
  const { cookies } = await import("next/headers");
  const cookieStore = await cookies();
  return getAllServerTimezones(cookieStore);
}

// Module-level cache of Intl.DateTimeFormat instances keyed by options tag plus timezone
const dateTimeFormatCache = new Map<string, Intl.DateTimeFormat>();

// Get-or-create the en-CA date-time formatter for a timezone; cached only after successful construction
function getCompanyDateTimeFormat(timezone: string): Intl.DateTimeFormat {
  const key = `en-CA-datetime|${timezone}`;
  let formatter = dateTimeFormatCache.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
    dateTimeFormatCache.set(key, formatter);
  }
  return formatter;
}

/**
 * Convert a UTC date to company timezone for API response
 * @param utcDate - Date object in UTC
 * @param timezone - Company timezone string
 * @returns Date string in company timezone
 */
/**
 * A date people read, in the company's own timezone: "9/21/2026, 7:57 AM".
 * Used for remarks and anything else shown to a CSR or a customer.
 */
export function formatDateTimeForCompany(date: Date, timezone?: string | null): string {
  if (!date) return "";
  try {
    return date.toLocaleString("en-US", {
      timeZone: timezone || "America/New_York",
      month: "numeric",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch (error) {
    console.warn(`Invalid timezone ${timezone}, using UTC:`, error);
    return date.toISOString();
  }
}

export function convertUTCToCompanyTimezone(
  utcDate: Date,
  timezone: string = "America/New_York",
): string {
  if (!utcDate || !timezone) return utcDate?.toISOString() ?? "";

  try {
    const parts = getCompanyDateTimeFormat(timezone).formatToParts(utcDate);

    const get = (type: string) => parts.find(p => p.type === type)?.value ?? "00";

    // pad helper ensures two digits
    const pad = (value: string) => value.padStart(2, "0");

    return `${pad(get("year"))}-${pad(get("month"))}-${pad(get("day"))}T${pad(get("hour"))}:${pad(get("minute"))}:${pad(get("second"))}`;
  } catch (error) {
    console.warn(`Invalid timezone ${timezone}, using UTC:`, error);
    return utcDate.toISOString();
  }
}

/**
 * Convert a UTC date to company timezone date only (no time)
 * @param utcDate - Date object in UTC
 * @param timezone - Company timezone string
 * @returns Date string in YYYY-MM-DD format in company timezone
 */
export function convertUTCToCompanyTimezoneDate(utcDate: Date, timezone: string): string {
  if (!utcDate || !timezone) {
    return utcDate ? utcDate.toISOString().split("T")[0] : "";
  }

  try {
    return utcDate.toLocaleDateString("en-CA", { timeZone: timezone }); // en-CA gives YYYY-MM-DD format
  } catch (error) {
    console.warn(`Invalid timezone ${timezone}, using UTC:`, error);
    return utcDate.toISOString().split("T")[0];
  }
}

/**
 * Helper function to safely get a nested property value
 * @param obj - Object to traverse
 * @param path - Dot-separated path (e.g., "job.schedule.start_time")
 * @returns The value at the path or undefined if path doesn't exist
 */
function getNestedValue(obj: any, path: string): any {
  return path.split(".").reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : undefined;
  }, obj);
}

/**
 * Helper function to safely set a nested property value
 * @param obj - Object to modify
 * @param path - Dot-separated path (e.g., "job.schedule.start_time")
 * @param value - Value to set
 */
function setNestedValue(obj: any, path: string, value: any): void {
  const keys = path.split(".");
  const lastKey = keys.pop()!;
  const target = keys.reduce((current, key) => {
    if (!current[key]) {
      current[key] = {};
    }
    return current[key];
  }, obj);
  target[lastKey] = value;
}

/**
 * Process an object and convert all date fields from UTC to company timezone
 * @param obj - Object containing date fields
 * @param timezone - Company timezone string
 * @param dateFields - Array of field names that should be converted (supports nested paths like "job.schedule.start_time")
 * @returns Object with converted dates
 */
export function convertObjectDatesToCompanyTimezone(
  obj: any,
  timezone: string,
  dateFields: string[] = [
    "created_at",
    "createdAt",
    "updated_at",
    "updatedAt",
    "date",
    "timestamp",
  ],
): any {
  if (!obj || !timezone) return obj;

  const result = { ...obj };

  dateFields.forEach(field => {
    const value = getNestedValue(result, field);
    if (value) {
      let dateToConvert: Date;

      if (value instanceof Date) {
        dateToConvert = value;
      } else if (
        typeof value === "string" &&
        (value.includes("T") || value.includes("Z") || value.includes("+00:00"))
      ) {
        // Handle date strings (ISO format, UTC format, etc.)
        dateToConvert = new Date(value);
        if (isNaN(dateToConvert.getTime())) {
          return; // Skip if invalid date
        }
      } else {
        return; // Skip if not a date
      }

      setNestedValue(result, field, convertUTCToCompanyTimezone(dateToConvert, timezone));
    }
  });

  return result;
}

/**
 * Process an array of objects and convert all date fields from UTC to company timezone
 * @param array - Array of objects containing date fields
 * @param timezone - Company timezone string
 * @param dateFields - Array of field names that should be converted
 * @returns Array with converted dates
 */
export function convertArrayDatesToCompanyTimezone(
  array: any[],
  timezone: string,
  dateFields: string[] = [
    "created_at",
    "createdAt",
    "updated_at",
    "updatedAt",
    "date",
    "timestamp",
    // "hcpJob.schedule.scheduled_start",
    "hcpJob.schedule.scheduled_end",
  ],
): any[] {
  if (!array || !timezone) return array;

  return array.map(obj => convertObjectDatesToCompanyTimezone(obj, timezone, dateFields));
}

// ===== FRONTEND TIMEZONE UTILITIES =====

/**
 * Create a date in the company's timezone
 * @param year - Year
 * @param month - Month (0-11)
 * @param day - Day of month
 * @param timezone - Company timezone string
 * @returns Date object representing the date in company timezone
 */
export function createDateInCompanyTimezone(
  year: number,
  month: number,
  day: number,
  timezone: string,
): Date {
  if (!timezone) {
    return new Date(year, month, day);
  }

  try {
    // Create a date string in the company timezone
    const dateString = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}T00:00:00`;

    // Parse the date in the company timezone
    const utcDate = new Date(dateString + "Z"); // Add Z to make it UTC

    // Convert to company timezone
    const companyDate = new Date(utcDate.toLocaleString("en-US", { timeZone: timezone }));

    return companyDate;
  } catch (error) {
    console.warn(`Invalid timezone ${timezone}, using local date:`, error);
    return new Date(year, month, day);
  }
}

/**
 * Get today's date in company timezone
 * @param timezone - Company timezone string
 * @returns Date object representing today in company timezone
 */
export function getTodayInCompanyTimezone(timezone: string): Date {
  if (!timezone) {
    return new Date();
  }

  try {
    const now = new Date();
    const todayString = now.toLocaleDateString("en-CA", { timeZone: timezone }); // YYYY-MM-DD format
    const [year, month, day] = todayString.split("-").map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed
  } catch (error) {
    console.warn(`Invalid timezone ${timezone}, using local date:`, error);
    return new Date();
  }
}

/**
 * Format a date to YYYY-MM-DD string in company timezone
 * @param date - Date object
 * @param timezone - Company timezone string
 * @returns Date string in YYYY-MM-DD format in company timezone
 */
export function formatDateToYYYYMMDDInTimezone(date: Date, timezone: string): string {
  if (!date || !timezone) {
    return date ? date.toISOString().split("T")[0] : "";
  }

  try {
    return date.toLocaleDateString("en-CA", { timeZone: timezone }); // en-CA gives YYYY-MM-DD format
  } catch (error) {
    console.warn(`Invalid timezone ${timezone}, using local date:`, error);
    return date.toISOString().split("T")[0];
  }
}

/**
 * Create a date from YYYY-MM-DD string in company timezone
 * @param dateString - Date string in YYYY-MM-DD format
 * @param timezone - Company timezone string
 * @returns Date object in company timezone
 */
export function createDateFromYYYYMMDDInTimezone(dateString: string, timezone: string): Date {
  if (!dateString || !timezone) {
    return dateString ? new Date(dateString) : new Date();
  }

  try {
    // Create date in company timezone
    const dateStringWithTime = `${dateString}T00:00:00`;
    const utcDate = new Date(dateStringWithTime + "Z"); // Add Z to make it UTC

    // Convert to company timezone
    const companyDate = new Date(utcDate.toLocaleString("en-US", { timeZone: timezone }));

    return companyDate;
  } catch (error) {
    console.warn(`Invalid timezone ${timezone} or date ${dateString}, using local date:`, error);
    return dateString ? new Date(dateString) : new Date();
  }
}

/**
 * Get date range in company timezone
 * @param daysBack - Number of days to go back from today
 * @param timezone - Company timezone string
 * @returns Object with start_date and end_date in YYYY-MM-DD format
 */
export function getDateRangeInTimezone(
  daysBack: number,
  timezone: string,
): { start_date: string; end_date: string } {
  const today = getTodayInCompanyTimezone(timezone);
  const startDate = new Date(today);
  startDate.setDate(today.getDate() - daysBack);

  return {
    start_date: formatDateToYYYYMMDDInTimezone(startDate, timezone),
    end_date: formatDateToYYYYMMDDInTimezone(today, timezone),
  };
}

/**
 * Get months back range in company timezone
 * @param monthsBack - Number of months to go back from today
 * @param timezone - Company timezone string
 * @returns Object with start_date and end_date in YYYY-MM-DD format
 */
export function getMonthsBackRangeInTimezone(
  monthsBack: number,
  timezone: string,
): { start_date: string; end_date: string } {
  const today = getTodayInCompanyTimezone(timezone);
  const startDate = new Date(today);
  startDate.setMonth(today.getMonth() - monthsBack);

  return {
    start_date: formatDateToYYYYMMDDInTimezone(startDate, timezone),
    end_date: formatDateToYYYYMMDDInTimezone(today, timezone),
  };
}

/**
 * Get yesterday's date in company timezone
 * @param timezone - Company timezone string
 * @returns Date string in YYYY-MM-DD format
 */
export function getYesterdayYYYYMMDDInTimezone(timezone: string): string {
  const today = getTodayInCompanyTimezone(timezone);
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  return formatDateToYYYYMMDDInTimezone(yesterday, timezone);
}

/**
 * Convert a date string (YYYY-MM-DD) to UTC start of day in the given timezone
 * @param dateString - Date in YYYY-MM-DD format
 * @param timezone - Timezone string (e.g., "America/New_York")
 * @returns UTC Date object representing start of day in the given timezone
 */
export function getStartOfDayUTC(dateString: string, timezone: string): Date {
  // Use zonedTimeToUtc for accurate conversion
  return zonedTimeToUtc(`${dateString}T00:00:00`, timezone);
}

/**
 * Convert a date string (YYYY-MM-DD) to UTC end of day in the given timezone
 * @param dateString - Date in YYYY-MM-DD format
 * @param timezone - Timezone string (e.g., "America/New_York")
 * @returns UTC Date object representing end of day in the given timezone
 */
export function getEndOfDayUTC(dateString: string, timezone: string): Date {
  // Use zonedTimeToUtc for accurate conversion
  // Use 23:59:59.999 to ensure we capture the entire day
  return zonedTimeToUtc(`${dateString}T23:59:59.999`, timezone);
}
interface ConvertOptions {
  startDay?: boolean;
  endDay?: boolean;
  normalDate?: boolean;
  resetTime?: boolean; // <-- new option
}

/**
 * Converts a date (string or Date) to a target timezone.
 * Works reliably even if input is system local time (any country).
 */
export function convertToTimezoneDateFinal(
  date: Date | string,
  timeZone: string,
  options: ConvertOptions = {},
): Date {
  if (!date) throw new Error("Date is required");
  if (!timeZone) throw new Error("timeZone is required");

  // Step 1: Ensure Date object
  const inputDate = typeof date === "string" ? new Date(date) : date;

  // Step 2: Convert the UTC timestamp to target timezone
  // This handles system local time correctly
  const utcDate = new Date(inputDate.getTime() - inputDate.getTimezoneOffset() * 60000);
  const zonedDate = utcToZonedTime(utcDate, timeZone);

  // Step 3: Apply start or end of day
  if (options.startDay) zonedDate.setHours(0, 0, 0, 0);
  else if (options.endDay) zonedDate.setHours(23, 59, 59, 999);
  else if (options.resetTime) {
    // Set hours, minutes, seconds, milliseconds to 0
    zonedDate.setHours(0, 0, 0, 0);
  }

  return zonedDate;
}

export const formatMinutesToHoursMinutes = (totalMinutes: number) => {
  if (totalMinutes < 60) {
    return `${totalMinutes} min`;
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return minutes === 0 ? `${hours} hr` : `${hours} hr ${minutes} min`;
};

export const toValidDate = (value: Date | string | null | undefined): Date => {
  if (!value) return new Date();
  return value instanceof Date ? value : new Date(value);
};
